/* eslint-disable sonarjs/no-duplicate-string */
import { z } from "zod";
import { fileSchema } from "../files/files.schema";
import { days, placeTypes } from "@ac-expo/api-types";
import { Day, PlaceType } from "@prisma/client";
import { DefaultValues } from "react-hook-form";
import { Time } from "@internationalized/date";
import { isBefore } from "date-fns";
import {
  defaultAddressValues,
  requiredCoordsAddressSchema,
} from "../utils/address.schema";

const INVALID_HOUR = "Heure invalide";

const zTime = z.instanceof(Time, { message: INVALID_HOUR });

const upsertTouristicPlaceTimetableSchema = z
  .object({
    id: z.string().optional(),
    day: z.enum(days as [Day, ...Day[]]),
    type: z.enum(["open-continuously", "open", "close"]),
    openingTime: zTime.nullish(),
    closingTime: zTime.nullish(),
    breakStartTime: zTime.nullish(),
    breakEndTime: zTime.nullish(),
  })
  .refine(({ type, openingTime }) => type === "close" || openingTime, {
    message: INVALID_HOUR,
    path: ["openingTime"],
  })
  .refine(
    ({ type, closingTime, openingTime, breakEndTime }) => {
      if (type === "close") return true;
      if (!closingTime) return false;

      if (type === "open-continuously")
        return !openingTime || closingTime.compare(openingTime) > 0;
      return !breakEndTime || closingTime.compare(breakEndTime) > 0;
    },
    {
      message: INVALID_HOUR,
      path: ["closingTime"],
    }
  )
  .refine(
    ({ type, breakStartTime, openingTime }) => {
      if (type !== "open") return true;
      if (!breakStartTime) return false;
      return !openingTime || breakStartTime.compare(openingTime) > 0;
    },
    {
      message: INVALID_HOUR,
      path: ["breakStartTime"],
    }
  )
  .refine(
    ({ type, breakEndTime, breakStartTime }) => {
      if (type !== "open") return true;
      if (!breakEndTime) return false;
      return !breakStartTime || breakEndTime.compare(breakStartTime) > 0;
    },
    {
      message: INVALID_HOUR,
      path: ["breakEndTime"],
    }
  );

export type TouristicPlaceTimetable = z.infer<
  typeof upsertTouristicPlaceTimetableSchema
>;

const upsertExceptionalOpeningSchema = z.object({
  id: z.string().optional(),
  date: z.date().refine((date) => !isNaN(date.getTime()), {
    message: "Date invalide",
  }),
  timetable: upsertTouristicPlaceTimetableSchema,
});

export type TouristicPlaceExceptionalOpeningForm = z.infer<
  typeof upsertExceptionalOpeningSchema
>;

export const defaultExceptionalOpeningsValues: DefaultValues<TouristicPlaceExceptionalOpeningForm> =
  {
    timetable: {},
  };

const upsertTouristicPlaceOpeningSchema = z
  .object({
    id: z.string().optional(),
    dates: z
      .object({
        from: z.date().refine((date) => !isNaN(date.getTime()), {
          message: "Date de début invalide",
        }),
        to: z.date().refine((date) => !isNaN(date.getTime()), {
          message: "Date de fin invalide",
        }),
      })
      .nullish(),
    allYear: z.boolean(),
    timetables: z.array(upsertTouristicPlaceTimetableSchema).refine(
      (data) => {
        const days = data.map((timetable) => timetable.day);
        return new Set(days).size === days.length;
      },
      {
        message: "Tous les jours doivent être renseignés",
      }
    ),
  })
  .refine(({ allYear, dates }) => (!allYear ? dates?.from && dates.to : true), {
    message: "Veuillez renseigner les dates d'ouverture",
  });

export type TouristicPlaceOpeningForm = z.infer<
  typeof upsertTouristicPlaceOpeningSchema
>;

export const defaultTouristicPlaceOpeningValues: TouristicPlaceOpeningForm = {
  allYear: true,
  timetables: days.map((day) => ({
    day,
    type: day === "sunday" ? "close" : "open",
  })),
};

export const upsertTouristicPlaceSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, "Veuillez entrer un nom"),
  description: z.string().min(2, "Veuillez entrer une description"),
  type: z.enum(placeTypes as [PlaceType, ...PlaceType[]], {
    message: "Veuillez sélectionner un type de lieu",
  }),
  websiteUrl: z.union([z.string().url(), z.string().length(0)]).nullish(),
  // TO-DO address schema -> see Valentin L. work after google map
  entry: z
    .enum(["free", "paid"], {
      required_error: "Veuillez sélectionner un type d'entrée",
    })
    .default("free"),
  venue: z
    .object({
      indoor: z.boolean(),
      outdoor: z.boolean(),
    })
    .refine((data) => data.indoor || data.outdoor, {
      message: "Au moins un type de lieu doit être renseigné",
    }),
  image: fileSchema
    .refine(({ key }) => key !== "", {
      message: "Veuillez sélectionner une image",
    })
    .nullish(),
  address: requiredCoordsAddressSchema,
  openings: z
    .array(upsertTouristicPlaceOpeningSchema)
    .superRefine((openings, ctx) => {
      if (openings[0].allYear) return;
      let hasMissingDates = false;
      openings.forEach((opening, index) => {
        if (!opening.dates || !opening.dates.from || !opening.dates.to) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Veuillez renseigner les dates d'ouverture",
            path: [index, "dates"],
          });
          hasMissingDates = true;
        }
      });
      if (hasMissingDates) return;
      const datesSorted = (
        openings.map((o) => o.dates) as { from: Date; to: Date }[]
      ).sort((a, b) => (isBefore(a.from, b.from) ? -1 : 1));

      for (let i = 1; i < datesSorted.length; i++) {
        const prevEnd = datesSorted[i - 1].to;
        const currentStart = datesSorted[i].from;
        // Check if the current start date is before or on the previous end date
        if (currentStart <= prevEnd) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Les plages de dates ne doivent pas se chevaucher",
            path: [i, "dates"],
          });
        }
      }
    }),
  exceptionalOpenings: z.array(upsertExceptionalOpeningSchema),
});

export type UpsertTouristicPlaceForm = z.infer<
  typeof upsertTouristicPlaceSchema
>;

export const defaultTouristicPlaceValues: DefaultValues<UpsertTouristicPlaceForm> =
  {
    type: undefined,
    image: undefined,
    name: "",
    description: "",
    address: defaultAddressValues,
    entry: "free",
    venue: {
      indoor: false,
      outdoor: false,
    },
    openings: [defaultTouristicPlaceOpeningValues],
    exceptionalOpenings: [],
  };
